import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Users,
  CheckCircle,
  PlayCircle,
  Stack,
  ChartBar,
  PencilSimple,
} from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { formatDuration } from "@/lib/analyticsFormat";

/**
 * Admin per-course analytics drill-down.
 * Backend: GET /api/analytics/courses/:courseId
 * Shows enrolled count, per-module + per-lesson watchers / completion rate / avg watched.
 */
export default function AdminCourseAnalytics() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/analytics/courses/${courseId}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(formatApiError(e, "Could not load course analytics."));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [courseId]);

  // Aggregate header stats from per-lesson rows
  const stats = useMemo(() => {
    if (!data) return null;
    const lessons = data.lessons || [];
    const totalWatchers = lessons.reduce((s, l) => s + (l.watchers || 0), 0);
    const totalCompleted = lessons.reduce((s, l) => s + (l.completed || 0), 0);
    const totalRuntime = lessons.reduce((s, l) => s + (l.duration_seconds || 0), 0);
    const avgCompletion =
      lessons.length === 0
        ? 0
        : Math.round(
            lessons.reduce((s, l) => s + (l.completion_rate || 0), 0) / lessons.length
          );
    return { totalWatchers, totalCompleted, totalRuntime, avgCompletion };
  }, [data]);

  // Group lessons by module title for the table
  const grouped = useMemo(() => {
    const g = [];
    let current = null;
    for (const l of data?.lessons || []) {
      if (!current || current.title !== l.module_title) {
        current = { title: l.module_title, lessons: [] };
        g.push(current);
      }
      current.lessons.push(l);
    }
    return g;
  }, [data]);

  return (
    <AppLayout
      title={data?.course?.title || (loading ? "Loading…" : "Course analytics")}
      eyebrow="Per-course engagement"
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/admin/analytics"
            data-testid="back-to-analytics"
            className="px-3 py-2 text-sm font-medium text-[#5C5C58] hover:text-[#1C1C19] inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
          {data?.course?.id && (
            <Link
              to={`/admin/courses/${data.course.id}`}
              data-testid="edit-course-from-analytics"
              className="px-4 py-2 bg-[#1C1C19] hover:bg-black text-white text-sm font-medium inline-flex items-center gap-2 rounded-md"
            >
              <PencilSimple size={14} /> Edit course
            </Link>
          )}
        </div>
      }
    >
      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Card icon={Users} label="Enrolled" value={loading ? "—" : data?.enrolled ?? 0} testid="course-card-enrolled" />
        <Card icon={Stack} label="Lessons" value={loading ? "—" : data?.lessons?.length ?? 0} testid="course-card-lessons" />
        <Card icon={CheckCircle} label="Avg completion" value={loading ? "—" : `${stats?.avgCompletion ?? 0}%`} testid="course-card-avg-completion" />
        <Card icon={ChartBar} label="Total runtime" value={loading ? "—" : formatDuration(stats?.totalRuntime ?? 0)} testid="course-card-runtime" />
      </div>

      {/* Per-lesson breakdown grouped by module */}
      <div className="bg-white border border-[#E5E2DC] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E2DC] flex items-center justify-between">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">Lesson engagement</div>
            <div className="text-xs text-[#5C5C58]">Completion rate & avg watch time per lesson, grouped by module</div>
          </div>
          <BookOpen size={20} weight="duotone" className="text-[#E25822]" />
        </div>

        {loading && (
          <div className="px-5 py-10 text-center text-[#8A8A85]">Loading…</div>
        )}

        {!loading && (data?.lessons || []).length === 0 && (
          <div className="px-5 py-10 text-center text-[#8A8A85] text-sm">
            This course has no lessons yet. Add some in the course editor first.
          </div>
        )}

        {!loading && grouped.length > 0 && (
          <table className="w-full text-sm" data-testid="course-lessons-table">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5C5C58]">
              <tr>
                <th className="px-5 py-3 text-left font-semibold w-1/2">Lesson</th>
                <th className="px-5 py-3 text-right font-semibold">Duration</th>
                <th className="px-5 py-3 text-right font-semibold">Watchers</th>
                <th className="px-5 py-3 text-right font-semibold">Completed</th>
                <th className="px-5 py-3 text-right font-semibold">Completion</th>
                <th className="px-5 py-3 text-right font-semibold">Avg watched</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((mod, mIdx) => (
                <React.Fragment key={`${mod.title}-${mIdx}`}>
                  <tr className="bg-[#FAF7F2]/60 border-t border-[#EFECE5]">
                    <td
                      colSpan={6}
                      className="px-5 py-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5C5C58]"
                    >
                      {String(mIdx + 1).padStart(2, "0")} · {mod.title || "Untitled module"}
                    </td>
                  </tr>
                  {mod.lessons.map((l) => (
                    <tr
                      key={l.lesson_id}
                      data-testid={`course-lesson-row-${l.lesson_id}`}
                      className="border-t border-[#EFECE5] hover:bg-[#FAF7F2]"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <PlayCircle size={16} weight="duotone" className="text-[#E25822] shrink-0" />
                          <span className="text-[#1C1C19]">{l.title}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right text-[#5C5C58]">{formatDuration(l.duration_seconds)}</td>
                      <td className="px-5 py-3 text-right text-[#5C5C58]">{l.watchers}</td>
                      <td className="px-5 py-3 text-right text-[#5C5C58]">{l.completed}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex flex-col items-end gap-1">
                          <span className="text-[#1C1C19] font-medium">{l.completion_rate}%</span>
                          <div className="w-24 h-1.5 bg-[#EFECE5] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#E25822]"
                              style={{ width: `${Math.min(100, l.completion_rate)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right text-[#5C5C58]">
                        {formatDuration(l.avg_watched_seconds)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}

function Card({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E2DC] p-5 rounded-md">
      <div className="flex items-center justify-between mb-3">
        <Icon size={20} weight="duotone" className="text-[#5C5C58]" />
      </div>
      <div className="font-heading text-3xl font-semibold text-[#1C1C19] mb-1">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#5C5C58] font-medium">{label}</div>
    </div>
  );
}
